import React, { useState, useCallback } from 'react';

const LS_PREFIX = 'sfx_ra_section_collapsed_';

// Each tint pairs a light bg (50) with a deeper hover (100), and a dark-mode
// bg with /20 base + /30 hover. Matches the library page's header-strip
// pattern (bg-gray-50 → bg-gray-100, dark: 700/30 → 700/50) but with per-
// section hues so each card reads as distinct without shouting.
const TINTS = {
  blue:    'bg-blue-50    hover:bg-blue-100    dark:bg-blue-900/20    dark:hover:bg-blue-900/30',
  amber:   'bg-amber-50   hover:bg-amber-100   dark:bg-amber-900/20   dark:hover:bg-amber-900/30',
  slate:   'bg-slate-50   hover:bg-slate-100   dark:bg-slate-700/30   dark:hover:bg-slate-700/50',
  emerald: 'bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/30',
  indigo:  'bg-indigo-50  hover:bg-indigo-100  dark:bg-indigo-900/20  dark:hover:bg-indigo-900/30',
  rose:    'bg-rose-50    hover:bg-rose-100    dark:bg-rose-900/20    dark:hover:bg-rose-900/30',
};

/**
 * Card with a clickable, tinted header strip that toggles a body region.
 * Mirrors the header pattern used on RiskAssessmentLibrary.js category groups
 * (gray-50 base, hover bumps to gray-100, chevron rotates -90deg when
 * collapsed), but with per-section colour tints.
 *
 *  - `id`: required, used for the localStorage key (sfx_ra_section_collapsed_<id>)
 *  - `title`: text shown in the header strip
 *  - `tint`: TINTS key — 'blue'|'amber'|'slate'|'emerald'|'indigo'|'rose'
 *  - `defaultCollapsed`: initial collapsed state when no localStorage entry exists
 *  - `headerExtra`: optional JSX rendered to the right of the title. Clicks
 *    inside it are stopped from propagating so they don't toggle the section.
 */
export default function CollapsibleCard({
  id,
  title,
  tint = 'slate',
  number = null,
  defaultCollapsed = false,
  headerExtra = null,
  children,
}) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_PREFIX + id);
      if (saved !== null) return saved === 'true';
    } catch { /* ignore */ }
    return defaultCollapsed;
  });

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(LS_PREFIX + id, String(next)); } catch { /* ignore */ }
      return next;
    });
  }, [id]);

  const tintClass = TINTS[tint] || TINTS.slate;

  return (
    <section
      className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
      data-testid={`ra-collapsible-${id}`}
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className={`w-full flex items-center justify-between px-4 py-2.5 min-h-[44px] text-left ${tintClass} transition-colors cursor-pointer`}
        data-testid={`ra-collapsible-toggle-${id}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          {number != null && (
            <span
              className="w-5 h-5 rounded-full bg-white/80 dark:bg-gray-800/70 text-[11px] font-bold text-gray-700 dark:text-gray-200 inline-flex items-center justify-center shrink-0 ring-1 ring-gray-300 dark:ring-gray-600"
              aria-hidden="true"
            >
              {number}
            </span>
          )}
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 truncate">{title}</h2>
          {headerExtra && (
            <div
              className="flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {headerExtra}
            </div>
          )}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 text-gray-500 dark:text-gray-400 shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {!collapsed && (
        // 📱 `max-md:px-2` (mobile batch item 14): one of the five nested
        // insets the RA hazards lost their width to. Desktop untouched.
        <div className="bg-white dark:bg-gray-800 p-4 max-md:px-2 border-t border-gray-200 dark:border-gray-700">
          {children}
        </div>
      )}
    </section>
  );
}
